// Product page runtime: gallery, variant dropdowns, sibling navigation, variant re-render via Section Rendering
// API, AJAX add-to-cart then redirect to /cart. Emits the same custom events apps may listen to on v2 (theme:*).
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-media-id]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.select(t.dataset.thumb)));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
    this.querySelector('[data-slides]')?.addEventListener('keydown', (e) => { if (e.key === 'ArrowLeft') this.step(-1); if (e.key === 'ArrowRight') this.step(1); });
  }
  get index() { return this.slides.findIndex((s) => s.classList.contains('is-active')); }
  step(d) { const n = this.slides.length; if (n) this.select(this.slides[(this.index + d + n) % n].dataset.mediaId); }
  select(id) {
    this.slides.forEach((s) => { const on = s.dataset.mediaId === String(id); s.classList.toggle('is-active', on); s.toggleAttribute('aria-hidden', !on); s.toggleAttribute('inert', !on); if (on && s.dataset.mediaType === 'video') s.querySelector('video')?.play?.().catch(() => {}); });
    this.thumbs.forEach((t) => { const on = t.dataset.thumb === String(id); t.classList.toggle('is-active', on); if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    this.dataset.activeMedia = id;
    if (window.matchMedia('(max-width: 749px)').matches) { const s = this.slides[this.index]; s?.parentElement.scrollTo({ left: s.offsetLeft - 20, behavior: 'smooth' }); }
    this.dispatchEvent(new CustomEvent('theme:media:select', { bubbles: true, detail: { id } }));
  }
}
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

class PopoutSelect extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-popout-toggle]'); this.list = this.querySelector('[data-popout-list]');
    this.input = this.querySelector('[data-option-input]'); this.label = this.querySelector('[data-popout-label]');
    this.toggle.addEventListener('click', () => this.open(this.list.hidden));
    this.list.addEventListener('click', (e) => { const li = e.target.closest('[role="option"]'); if (li) this.choose(li); });
    this.list.addEventListener('keydown', (e) => { const items = [...this.list.children]; const i = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); items[Math.min(i + 1, items.length - 1)]?.focus(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); items[Math.max(i - 1, 0)]?.focus(); }
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.choose(document.activeElement); }
      if (e.key === 'Escape') { this.open(false); this.toggle.focus(); } });
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.open(false); });
  }
  open(on) {
    this.list.hidden = !on; this.toggle.setAttribute('aria-expanded', String(on));
    if (on) { const r = this.toggle.getBoundingClientRect(); this.list.classList.toggle('popout__list--up', r.top > window.innerHeight / 2); (this.list.querySelector('[aria-selected="true"]') || this.list.firstElementChild)?.focus(); }
  }
  choose(li) {
    if (!li) return;
    [...this.list.children].forEach((x) => x.removeAttribute('aria-selected')); li.setAttribute('aria-selected', 'true');
    this.label.textContent = li.dataset.value; this.open(false); this.toggle.focus();
    this.closest('.picker__option')?.querySelector('[data-selected-value]')?.replaceChildren(li.dataset.value);
    // A COLOURWAY item carries the sibling product's URL and has no option input: drive the hidden
    // <select> that already owns this behaviour instead of navigating here. That select is what
    // restore() re-seats on a bfcache return (the 2026-09-05 fix), so leaving it as the source of
    // truth means this popout is a skin and not a second implementation to keep in step.
    if (li.dataset.url) {
      const sel = this.closest('[data-variant-picker]')?.querySelector('[data-sibling-select]');
      if (sel) { sel.value = li.dataset.url; sel.dispatchEvent(new Event('change', { bubbles: true })); }
      else window.location.href = li.dataset.url;
      return;
    }
    this.input.value = li.dataset.value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
if (!customElements.get('popout-select')) customElements.define('popout-select', PopoutSelect);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.querySelector('[data-sibling-select]')?.addEventListener('change', (e) => { if (e.target.value) window.location.href = e.target.value; });
    this.addEventListener('change', (e) => { if (e.target.matches('[data-option-input]')) this.onOptions(); });
    // What the SERVER rendered, kept for a bfcache restore.
    this.initial = [...this.querySelectorAll('[data-option-input]')].map((i) => [i, i.value]);
    this.initialSibling = this.querySelector('[data-sibling-select]')?.value || null;
  }
  restore() {
    // Back/forward returns this page from the bfcache with the form controls exactly as the shopper left them — including
    // the colourway select they used to navigate AWAY, so the page showed another product's colour and a stale buy state
    // (owner, 2026-09-05). Re-seat every control on this page's own product, preferring ?variant= if the URL carries one.
    const sib = this.querySelector('[data-sibling-select]');
    if (sib) {
      const here = [...sib.options].find((o) => { try { return new URL(o.value, location.origin).pathname === location.pathname; } catch (e) { return false; } });
      sib.value = here ? here.value : (this.initialSibling ?? sib.value);
      // The select is hidden and the popout is what the shopper sees, so re-seating one without the
      // other would leave the page showing a colourway it is not on — the same defect this restore
      // exists to fix, moved one element across.
      const pop = this.querySelector('.picker__option--siblings [data-popout-list]');
      const item = pop && [...pop.children].find((li) => li.dataset.url === sib.value);
      if (item) {
        [...pop.children].forEach((x) => x.removeAttribute('aria-selected')); item.setAttribute('aria-selected', 'true');
        this.querySelector('.picker__option--siblings [data-popout-label]')?.replaceChildren(item.dataset.value);
        this.querySelector('.picker__option--siblings [data-selected-value]')?.replaceChildren(item.dataset.value);
      }
    }
    const wanted = new URLSearchParams(location.search).get('variant');
    const v = wanted && this.variants.find((x) => String(x.id) === String(wanted));
    if (v) this.querySelectorAll('[data-option-input]').forEach((i) => { const val = v[i.dataset.index]; if (val != null) i.value = val; });
    else this.initial?.forEach(([i, val]) => { i.value = val; });
    this.querySelectorAll('.picker__option').forEach((opt) => {
      const input = opt.querySelector('[data-option-input]'); if (!input) return;
      opt.querySelector('[data-selected-value]')?.replaceChildren(input.value);
      opt.querySelector('[data-popout-label]')?.replaceChildren(input.value);
      opt.querySelectorAll('[role="option"]').forEach((li) => li.setAttribute('aria-selected', String(li.dataset.value === input.value)));
    });
    this.onOptions();
  }
  onOptions() {
    // Match on option POSITION (data-index="optionN"): the colour option is not rendered when siblings carry it,
    // so the inputs are not a dense list — a positional zip mis-resolved every variant (staging v0.7.4).
    const chosen = [...this.querySelectorAll('[data-option-input]')].map((i) => [i.dataset.index, i.value]);
    const v = this.variants.find((x) => chosen.every(([key, val]) => x[key] === val));
    this.dispatchEvent(new CustomEvent('theme:variant:change', { bubbles: true, detail: { variant: v || null } }));
  }
}
if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.addEventListener('theme:variant:change', (e) => this.render(e.detail.variant));
    this.form = this.querySelector('form[data-product-form]');
    if (this.form) { this.form.dataset.submitBound = '1'; this.form.addEventListener('submit', (e) => this.add(e)); }
    this.recordRecentlyViewed();
    // Back/forward can restore this page from the bfcache with the buy block showing a variant the picker no longer
    // has selected (owner: "back" showed Add to cart on a sold-out size). Re-render from the picker on restore.
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.querySelector('variant-picker')?.restore?.(); });
  }
  get variantState() {
    if (this._vstate === undefined) {
      try { this._vstate = JSON.parse(this.querySelector('[data-variant-state]')?.textContent || 'null'); }
      catch (e) { this._vstate = null; }
    }
    return this._vstate;
  }
  async render(variant) {
    const btn = this.querySelector('[data-add-to-cart]'); const text = this.querySelector('[data-add-to-cart-text]');
    if (!variant) { btn.disabled = true; text.textContent = window.rosae.strings.unavailable; this.querySelector('[data-price-wrap]').hidden = true; return; }
    const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
    const state = this.variantState && this.variantState[variant.id];
    if (state) {
      // Server-rendered state, no fetch: instant, and the button node (with whatever an app mounted on it) survives.
      const input = this.querySelector('[name="id"]'); const priceWrap = this.querySelector('[data-price-wrap]');
      if (input) { input.value = variant.id; input.disabled = !state.available && !state.preorder; }
      btn.disabled = !state.available && !state.preorder;
      btn.dataset.preorder = String(state.preorder);
      const label = state.preorder ? window.rosae.strings.preorder : (state.available ? window.rosae.strings.addToCart : window.rosae.strings.soldOut);
      if (label && text.textContent !== label) text.textContent = label;
      if (priceWrap) { priceWrap.hidden = false; priceWrap.innerHTML = state.price; }
      const pre = this.querySelector('[data-product-preorder]');
      if (state.preorder && !pre && input) input.insertAdjacentHTML('afterend', `<input type="hidden" name="properties[${window.rosae.strings.saleType}]" value="${window.rosae.strings.preorder}" data-product-preorder>`);
      if (!state.preorder && pre) pre.remove();
      if (variant.featured_media) this.querySelector('product-gallery')?.select(variant.featured_media.id);
      // Apps that take the buy button over (RB Wishlist's back-in-stock, RB Reservations' held-state overlay) used to
      // re-mount because the subtree was replaced. It no longer is, so the event carries what they need to re-evaluate.
      this.dispatchEvent(new CustomEvent('theme:product-info:loaded', { bubbles: true, detail: { variant, available: state.available, soldOut: !state.available && !state.preorder, preorder: state.preorder, button: btn } }));
      return;
    }
    const res = await fetch(`${this.dataset.sectionUrl}&variant=${variant.id}`); const html = new DOMParser().parseFromString(await res.text(), 'text/html');
    const freshPrice = html.querySelector('[data-price-wrap]'); const curPrice = this.querySelector('[data-price-wrap]');
    if (freshPrice && curPrice) curPrice.replaceWith(freshPrice);
    // Patch the buy block IN PLACE rather than replacing the subtree. Replacing it detached everything the apps had
    // mounted on the button — RB Wishlist's back-in-stock takeover most of all, so on a sold-out size the shopper got a
    // dead "ÉPUISÉ" and every signup had to be made on the default size (owner, 2026-09-05). Fall back to a swap only
    // if the expected nodes are missing.
    const freshBtns = html.querySelector('[data-buttons-wrap]'); const curBtns = this.querySelector('[data-buttons-wrap]');
    if (freshBtns && curBtns) {
      const fb = freshBtns.querySelector('[data-add-to-cart]'); const cb = curBtns.querySelector('[data-add-to-cart]');
      const fi = freshBtns.querySelector('[name="id"]'); const ci = curBtns.querySelector('[name="id"]');
      if (fb && cb && fi && ci) {
        ci.value = fi.value; ci.disabled = fi.disabled;
        cb.disabled = fb.disabled;
        cb.dataset.preorder = fb.dataset.preorder || 'false';
        const ft = fb.querySelector('[data-add-to-cart-text]'); const ct = cb.querySelector('[data-add-to-cart-text]');
        if (ft && ct && ct.textContent !== ft.textContent) ct.textContent = ft.textContent;
        const fp = freshBtns.querySelector('[data-product-preorder]'); const cp = curBtns.querySelector('[data-product-preorder]');
        if (fp && !cp) ci.insertAdjacentElement('afterend', fp.cloneNode(true)); else if (!fp && cp) cp.remove();
      } else {
        curBtns.replaceWith(freshBtns);
      }
    }
    this.form = this.querySelector('form[data-product-form]');
    if (this.form && !this.form.dataset.submitBound) { this.form.dataset.submitBound = '1'; this.form.addEventListener('submit', (e) => this.add(e)); }
    if (variant.featured_media) this.querySelector('product-gallery')?.select(variant.featured_media.id);
    this.dispatchEvent(new CustomEvent('theme:product-info:loaded', { bubbles: true, detail: { variant } }));
  }
  async add(e) {
    e.preventDefault();
    const form = e.currentTarget; const btn = form.querySelector('[data-add-to-cart]'); const err = form.querySelector('[data-cart-errors-container]');
    btn.classList.add('is-loading'); btn.querySelector('.btn__loader').hidden = false; err.hidden = true;
    try {
      const res = await fetch(window.rosae.routes.cart_add + '.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'error');
      document.dispatchEvent(new CustomEvent('theme:product:add', { detail: data }));
      const cart = await (await fetch(window.rosae.routes.cart + '.js')).json();
      document.dispatchEvent(new CustomEvent('rosae:cart:updated', { detail: cart }));
      if (window.rosae.cartType === 'drawer' && document.querySelector('cart-drawer')) { document.dispatchEvent(new CustomEvent('theme:cart:refresh')); document.dispatchEvent(new CustomEvent('rosae:cart:open')); }
      else window.location.href = window.rosae.routes.cart;
    } catch (ex) {
      err.hidden = false; err.querySelector('[data-cart-error-message]').textContent = ex.message;
      document.dispatchEvent(new CustomEvent('theme:product:add-error', { detail: { message: ex.message } }));
    } finally { btn.classList.remove('is-loading'); btn.querySelector('.btn__loader').hidden = true; }
  }
  recordRecentlyViewed() {
    // Parity with v2: cookie shopify_recently_viewed, space-separated handles, 10 max, 90 days.
    const h = this.dataset.productHandle; if (!h) return;
    const m = document.cookie.match(/(?:^|; )shopify_recently_viewed=([^;]*)/); let list = m ? decodeURIComponent(m[1]).split(' ').filter(Boolean) : [];
    list = [h, ...list.filter((x) => x !== h)].slice(0, 10);
    document.cookie = `shopify_recently_viewed=${encodeURIComponent(list.join(' '))}; path=/; max-age=${90 * 86400}; SameSite=Lax`;
  }
}
if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((t) => { t.addEventListener('click', () => this.show(t)); t.addEventListener('keydown', (e) => { const i = this.tabs.indexOf(t); if (e.key === 'ArrowRight') this.show(this.tabs[(i + 1) % this.tabs.length], true); if (e.key === 'ArrowLeft') this.show(this.tabs[(i - 1 + this.tabs.length) % this.tabs.length], true); }); });
  }
  show(tab, focus) {
    this.tabs.forEach((t) => { const on = t === tab; t.classList.toggle('is-current', on); t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; this.querySelector(`[data-panel="${t.dataset.tab}"]`).hidden = !on; });
    tab.scrollIntoView({ inline: 'nearest', block: 'nearest' }); if (focus) tab.focus();
  }
}
if (!customElements.get('product-tabs')) customElements.define('product-tabs', ProductTabs);
